﻿import React, { useState, useEffect, Fragment } from 'react';

import { Intent, Icon } from '@blueprintjs/core';
import { FormHelperText, FormSuggest, FormCheckboxList } from "components/form-fields";
import { Button } from "components/buttons";
import { NotificationToaster, AlertConfirm, NotificationInline, AlertUnsavedData } from "components/notifications";
import { ShowHide } from 'components/layout';
import { Wizard } from "components/wizard";
import classNames from 'classnames';
import PropTypes from 'prop-types';
import './Wizard.css';

const defaultObject = {};

export function MigrationWizard(
    {
        targetEntityTitle = 'Entity',
        loading = false,
        saving = false,
        selectedTargetEntity = defaultObject,
        entitySelectionWarning = '',
        entitySelectionWarnings = [],
        entities,
        getTransferConfirmationList,
        isWizardDirty,
        onEntitySelected,
        onReset,
        onTargetEntitySelect,
        onTransferCancel,
        onTransferConfirm,
        sourceEntityName,
        targetEntityList
    }
) {
	const [showTransferConfirm, setShowTransferConfirm] = useState(false);
	const [wizardStep, setWizardStep] = useState(0);
	const [wizardValid, setWizardValid] = useState(false);
	const [wizardFinished, setWizardFinished] = useState(false);
	const [transferSuccessful, setTransferSuccessful] = useState(false);
	const [loadingAffectedEntityCount, setLoadingAffectedEntityCount] = useState(false);
	const [totalCountZero, setTotalCountZero] = useState(false);
	const [confirmationMessageList, setConfirmationMessageList] = useState([]);

	const [wizardSteps, setWizardSteps] = useState([]);
	const [selectedEntities, setSelectedEntities] = useState([]);

	useEffect(setAllWizardSteps, [selectedEntities, selectedTargetEntity, targetEntityTitle, sourceEntityName, loading, targetEntityList, entitySelectionWarning]);

	useEffect(setIfWizardValid, [wizardStep, wizardSteps]);

	useEffect(() => {
		const newSelectedEntities = entities
			.filter(x => x.checked);

		setSelectedEntities([...newSelectedEntities]);
	}, [entities]);

	function setAllWizardSteps() {
		const defaultWizardSteps = [
			{
				key: 'TARGET-SELECTOR',
				valid: !!selectedTargetEntity?.id,
				step: <div>
					<h2>Select the target {targetEntityTitle.toLowerCase()}</h2>
					<FormSuggest
						loading={loading}
						items={targetEntityList}
						selectedValue={selectedTargetEntity?.id}
						onItemSelect={onTargetEntitySelectValue}
						placeholder={`Select ${targetEntityTitle.toLowerCase()} to transfer to`}
						headingText={`Select the ${targetEntityTitle.toLowerCase()} to which you want to migrate to:`}
					/>
				</div>
			},
			{
				key: 'ENTITY-SELECTOR',
				valid: selectedEntities.length > 0,
				step: <div>
					<h2>Which entities would you like to migrate?</h2>
					<h4>From {targetEntityTitle}</h4>
					<FormHelperText>{sourceEntityName}</FormHelperText>
					<h4>To {targetEntityTitle}:</h4>
					<FormHelperText>{selectedTargetEntity != null ? selectedTargetEntity.name : ""}</FormHelperText>

					<FormCheckboxList
						id="checklist-services-to-migrate"
						items={entities}
						onChange={handleCheckboxChange}
						headingText="Entities to migrate:"
						loading={loading}
						disabled={saving}
					>
					</FormCheckboxList>
					<NotificationInline
						id="notification-settings-disabled"
						loading={loading}
						show={!!entitySelectionWarning || entitySelectionWarnings.length > 0}
						text={entitySelectionWarning}
						messages={entitySelectionWarnings}
						intent="warning"
						allowClose={false}>
					</NotificationInline>
				</div>
			}
		];

		const additionalWizardSteps = selectedEntities
			.filter(x => x.wizardSteps)
			.map(x => x.wizardSteps)
			.flat();

		const allSteps = [...defaultWizardSteps.concat([...additionalWizardSteps])];

		setWizardSteps(allSteps);
	}

	function setIfWizardValid() {
		const valid = !!wizardSteps[wizardStep]?.valid;
		setWizardValid(valid);
	}

	function onTargetEntitySelectValue(item) {
		onTargetEntitySelect(item);
	}

	function resetComponent() {
		setWizardStep(0);
		setWizardValid(false);
		setWizardFinished(false);
		setTransferSuccessful(false);

		onReset();
	}

	function handleCheckboxChange(event, id) {
		onEntitySelected(event.target.checked, id);
	}

	function onWizardChange(step) {
		setWizardStep(step);
	}

	function onTransferEntities() {
		setShowTransferConfirm(true);		
		setLoadingAffectedEntityCount(true);

		getTransferConfirmationList()
			.then((confirmationList) => {
				setConfirmationMessageList(confirmationList);
				var total = getTotalConfirmationCount(confirmationList);
				setTotalCountZero(total === 0);
				setLoadingAffectedEntityCount(false);
			}, () => {
				NotificationToaster.show(Intent.DANGER, "Could not load the confirmation message. Please try again.");
			});
	}

	function getTotalConfirmationCount(confirmationList) {
		var total = 0;
		confirmationList.forEach(confirmation => total = total + confirmation.props.children[0]);
		return total;
    }
	function onTransferEntitiesConfirm() {
		setShowTransferConfirm(false);

		onTransferConfirm().then(() => {
			setWizardFinished(true);
			setTransferSuccessful(true);

			onReset();
		}, () => {
			setWizardFinished(true);
			setTransferSuccessful(false);

			onReset();
		});
	}

	function onTransferEntitiesCancel() {
		setShowTransferConfirm(false);
		onTransferCancel();
	}

	return (
        <div className="row">
            <ShowHide
				evaluator={!wizardFinished}
				show={(
					<Fragment>
						<Wizard loading={loading} onStepChange={onWizardChange} onFinish={onTransferEntities} canProceed={wizardValid} disabled={saving}>
							{wizardSteps.map(x => <Fragment key={x.key}>{x.step}</Fragment>)}
						</Wizard>
					</Fragment>
				)}
				hide={(
					<div>
						<ShowHide
							evaluator={transferSuccessful}
							show={(
								<Fragment>
									<div className="spacer-bottom" id="create-success-icon">
										<Icon icon="tick-circle" size={40} intent={Intent.SUCCESS} />
									</div>
									<h2 id="create-success-name">The migration is being processed</h2>
									<ShowHide
										evaluator={selectedEntities.some(x => x.successMessage)}
										show={(<div className="spacer-bottom">
											<p>Please note that:</p>
											<ul>
												{selectedEntities.filter(x => x.successMessage).map(x => <li key={x.key}>{x.successMessage}</li>)}
											</ul>
										</div>)}
									/>
								</Fragment>
							)}
							hide={(
								<Fragment>
									<div className="spacer-bottom" id="create-error-icon">
										<Icon icon="error" size={40} intent={Intent.DANGER} />
									</div>
									<h2 id="create-error-name">Some or all of the transfers were not completed</h2>
									<div className="spacer-bottom">
										<p>Please manually investigate to see which entities have been transferred and then run the wizard again.</p>
									</div>
								</Fragment>
							)}
						/>

						<div className="button-row" id="migration-complete-buttons">
							<Button intent="primary" text="Restart migration" id="restart-migration" onClick={resetComponent} />
						</div>

					</div>
				)}
			/>
            <AlertConfirm
				title="Please confirm"
				isOpen={showTransferConfirm}
				onConfirm={onTransferEntitiesConfirm}
				onCancel={onTransferEntitiesCancel}
				disabled={loadingAffectedEntityCount || totalCountZero}
			>
				<div className={classNames({ 'bp6-skeleton': loadingAffectedEntityCount })}>
					<p>Are you sure you want to transfer these entities? This operation will transfer:</p>
					<ul>
						{confirmationMessageList.map(x => <li key={x}>{x}</li>)}
					</ul>
					<strong>Once transferred this cannot be undone.</strong>
				</div>
			</AlertConfirm>
            <AlertUnsavedData
				isDirty={isWizardDirty}>
			</AlertUnsavedData>
        </div>
    );
}

MigrationWizard.propTypes = {
	entities: PropTypes.array.isRequired,
	onTargetEntitySelect: PropTypes.func.isRequired,
	targetEntityList: PropTypes.array.isRequired,
	onReset: PropTypes.func.isRequired,
	onEntitySelected: PropTypes.func.isRequired,
	getTransferConfirmationList: PropTypes.func.isRequired,
	onTransferConfirm: PropTypes.func.isRequired,
	onTransferCancel: PropTypes.func.isRequired,
	sourceEntityName: PropTypes.string.isRequired,
	isWizardDirty: PropTypes.bool.isRequired,
	targetEntityTitle: PropTypes.string,
	loading: PropTypes.bool,
	saving: PropTypes.bool,
	selectedTargetEntity: PropTypes.object,
	entitySelectionWarning: PropTypes.string,
	entitySelectionWarnings: PropTypes.array
};